import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { useAuth } from '../lib/auth';

interface CaseRow {
  id: string;
  name: string;
  description: string | null;
  status?: string;
  created_by_name: string;
  member_count: number;
  source_count: number;
  evidence_count?: number;
  open_tasks?: number;
  updated_at: string;
}

export function CasesPage() {
  const { user } = useAuth();
  const [cases, setCases] = useState<CaseRow[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const data = await api.get<{ cases: CaseRow[] }>('/api/cases');
    setCases(data.cases);
  };

  useEffect(() => {
    void load().catch((e) => setError(e instanceof Error ? e.message : 'Laden mislukt'));
  }, []);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    await api.post('/api/cases', { name: name.trim(), description: description.trim() || undefined });
    setName('');
    setDescription('');
    await load();
  };

  return (
    <div>
      <h1>Zaken</h1>
      <p className="page-lead">
        Open een zaak voor dossier, tijdlijn, entiteiten, alerts, taken en netwerk.
      </p>

      <form className="panel toolbar" onSubmit={(e) => void create(e)}>
        <div className="field" style={{ flex: 1 }}>
          <label>Naam</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field" style={{ flex: 2 }}>
          <label>Omschrijving</label>
          <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <button className="btn" type="submit">
          Zaak aanmaken
        </button>
      </form>

      {error && <div className="alert error">{error}</div>}

      <div className="panel" style={{ marginTop: 14, padding: 0, overflow: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Naam</th>
              <th>Status</th>
              <th>Bronnen</th>
              <th>Dossier</th>
              <th>Taken</th>
              <th>Bijgewerkt</th>
            </tr>
          </thead>
          <tbody>
            {cases.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to={`/cases/${c.id}`}>
                    <strong>{c.name}</strong>
                  </Link>
                  {c.description && <div className="muted">{c.description}</div>}
                  <div className="muted">{c.member_count} leden · {c.created_by_name}</div>
                </td>
                <td>
                  <span className={`badge ${c.status === 'open' ? 'ok' : 'warn'}`}>{c.status || 'open'}</span>
                </td>
                <td>{c.source_count}</td>
                <td>{c.evidence_count ?? 0}</td>
                <td>{c.open_tasks ?? 0}</td>
                <td className="muted">{formatDateTime(c.updated_at)}</td>
              </tr>
            ))}
            {cases.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  Nog geen zaken.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {user ? null : null}
    </div>
  );
}
